<template>
  <div class="saffron-app" :dir="isPageRtl ? 'rtl' : 'ltr'" v-bind="$attrs">
    <metainfo>
      <template #title="input">
        {{
          input.content
            ? input.content + " | " + input.metainfo.sitename.content
            : input.metainfo.sitename.content
        }}
      </template>
    </metainfo>
    <noscript v-if="useGtm && !isSSR">
      <iframe
        title="gtm-fallback"
        :src="`https://www.googletagmanager.com/ns.html?id=${gtmId}`"
        height="0"
        width="0"
        style="display: none; visibility: hidden"></iframe>
    </noscript>
    <slot name="top"></slot>

    <bar-loader
      v-if="useAsyncLoadBar"
      class="z-level-top-2"
      :active="asyncStatus?.anyLoading"
      :fixed="true"></bar-loader>

    <suspense>
      <div v-if="useLayouts" class="saffron-app-inner">
        <component
          :is="finalLayout"
          :key="finalLayout"
          :class="routerTransitionClassIn"
          v-bind="layoutParams">
          <slot>
            <router-view
              @page-component-mounted="handlePageMounted"
              v-slot="{ Component }"
              :key="$route.href">
              <transition
                mode="out-in"
                :enter-active-class="routerTransitionClassIn"
                :leave-active-class="routerTransitionClassOut">
                <component
                  :is="Component"
                  v-if="Component"
                  :key="`inner-${$route.href}`"
                  class="" />
              </transition>
            </router-view>
          </slot>
        </component>
      </div>
      <template #fallback="">
        <spinner :overlay="'fixed'"></spinner>
      </template>
    </suspense>
    <suspense>
      <div v-if="!useLayouts" class="saffron-app-inner">
        <slot>
          <router-view
            @page-component-mounted="handlePageMounted"
            v-slot="{ Component }"
            :key="$route.fullPath">
            <transition
              mode="out-in"
              :enter-active-class="routerTransitionClassIn"
              :leave-active-class="routerTransitionClassOut">
              <component
                :is="Component"
                v-if="Component"
                :class="routerTransitionClassIn" />
            </transition>
          </router-view>
        </slot>
      </div>
      <template #fallback="">
        <spinner :overlay="'fixed'"></spinner>
      </template>
    </suspense>

    <slot name="bottom"></slot>
    <site-credits
      v-if="computedShowSiteCredits"
      v-bind="siteCreditsProps"></site-credits>
  </div>

  <slot name="after-saffron-app"></slot>
  <inactivity-disconnect></inactivity-disconnect>

  <teleport to="#global-spinner" v-if="useGlobalSpinner">
    <spinner
      v-if="useGlobalSpinner"
      class="z-level-top-5"
      overlay="fixed"
      :text-params="globalSpinnerTextParams"
      :show="globalSpinnerActive"
      :text="globalSpinnerText"></spinner>
  </teleport>

  <modal
    ref="authErrorModal"
    :title="translate('core.user.clientSideAuthErrorTitle')"
    :show="showDisconnectedModal">
    <template #default>
      {{ translate("core.user.clientSideAuthErrorExplain") }}
    </template>
    <template #footer>
      <form-button @click="acceptDisconnect"
        >{{ translate("core.user.clientSideAuthErrorOk") }}
      </form-button>
    </template>
  </modal>

  <modal
    v-for="conf in modals"
    :key="conf.uid"
    :ref="`modal${conf.uid}`"
    :close-button="conf.type !== 'confirm'"
    :easy-close="conf.type !== 'confirm'"
    :show="true"
    :auto-translate="false"
    :title="conf.title"
    @modal:afterClose="
      modalStackCloseHandler({
        uid: conf.uid,
        actionType: $event.context || 'reject',
        data: conf.promptValue,
      })
    ">
    <template #default="{ modal }">
      <div
        class=""
        v-bind="conf.options.contentProps"
        v-html="sanitizeHtml(conf.content)"></div>
      <div
        v-if="conf.content && conf.content !== '' && conf.type === 'prompt'"
        class="margin-l-bottom"></div>

      <div v-if="conf.type === 'prompt'" class="">
        <form-input
          v-bind="conf.options.field"
          v-model="conf.promptValue"
          :focus-on-mount="true"
          @keyup.enter="modal.close('fulfil')"></form-input>
      </div>
    </template>
    <template #footer="{ modal }">
      <div class="flex flex-end gap-m width-expand">
        <form-button
          v-if="conf.type === 'confirm' || conf.type === 'prompt'"
          theme="gray-5"
          icon="x"
          v-bind="
            conf.options.cancelButtonProps ? conf.options.cancelButtonProps : {}
          "
          @click="modal.close('reject')"
          >{{ conf.options.labels.cancel }}
        </form-button>
        <form-button
          theme="success"
          icon-end="chevron-inline-end"
          v-bind="
            conf.options.confirmButtonProps
              ? conf.options.confirmButtonProps
              : {}
          "
          @click="modal.close('fulfil')">
          {{ conf.options.labels.ok }}
        </form-button>
      </div>
    </template>
  </modal>

  <notification
    v-for="(conf, id) of notifications"
    v-bind="getSafeNotificationConfig(conf)"
    :key="id"
    @notification:afterClose="notificationClosedHandler(id)"></notification>

  <div v-if="hasDebugItems && isDev">
    <teleport :to="'#debug'">
      <div
        @click="isShowingDebug = !isShowingDebug"
        style="top: 120px; inset-inline-start: 20px; width: 50px"
        class="debug-floater cursor-pointer z-level-top-5 fixed round border-thin-gray-1 bg-lead c-bg flex-middle flex-center">
        <icon icon="code" size="2"></icon>
      </div>
    </teleport>
    <drawer
      v-model="isShowingDebug"
      location="bottom"
      title="core.dev.debug.drawerTitle">
      <template #header-title>
        <div class="flex-center width-expand gap-m flex-middle">
          <icon-halo class="text-xl" icon="code" halo-theme="lead" theme="bg" />
          <span>{{ tt("core.dev.debug.drawerTitle") }}</span>
        </div>
      </template>
      <div class="ltr">
        <div v-for="item in debugItems">
          <h3 class="margin-s-bottom flex gap-s flex-middle">
            <span v-if="isNonEmptyString(item.label)">{{ item.label }} </span
            ><span class="text-m"> ({{ item.identifier }})</span>
          </h3>
          <debug
            class="bg-gray-1 padding border-radius-m margin-l-bottom"
            :deep="1"
            :collapsed-node-length="25"
            :data="item.data"
            v-bind="item.conf"></debug>
        </div>
      </div>
    </drawer>
  </div>

  <div class="floating-widgets">
    <icon-halo
      v-if="$acl.isSuperAdmin && !isAdminPage"
      class="cursor-pointer"
      @click="$router.push({ name: 'admin' })"
      v-tooltip="{ content: 'core.adminFloatingBadge.tooltip' }"
      size="1.5"
      halo-theme="inverse"
      theme="bg"
      icon="shield"
      v-bind="adminBadgeProps"></icon-halo>

    <icon-halo
      v-if="
        !$acl.isSuperAdmin && !isAdminPage && !!elevatedPermissionAdditionalUser
      "
      class="cursor-pointer"
      v-tooltip="{ content: 'core.elevatedUserFloatingBadge.tooltip' }"
      size="1.5"
      halo-theme="warning"
      theme="bg"
      icon="shield"
      @click="relogAsAdmin"
      v-bind="adminBadgeProps"></icon-halo>

    <div v-if="showNetworkQuality && !userRequestsToHideNetworkQuality">
      <icon-halo
        v-tooltip="'core.networkQuality.loadingNetworkStatusTooltip'"
        @dblclick="userRequestsToHideNetworkQuality = true"
        v-if="showNetworkQuality && !isNetworkQualityChecked"
        :size="1.5"
        halo-theme="lead"
        :theme="'bg'"
        :icon="false">
        <spinner :size="1" theme="bg"></spinner>
      </icon-halo>

      <icon-halo
        v-tooltip="{
          content: networkQualityDisplayData.tooltipText,
          autoTranslate: false,
        }"
        @dblclick="userRequestsToHideNetworkQuality = true"
        v-if="showNetworkQuality && isNetworkQualityChecked"
        :size="1.5"
        :halo-theme="networkQualityDisplayData.theme"
        :theme="'bg'"
        :icon="
          networkQualityDisplayData.isOk ? 'wifi' : 'wifi-disabled'
        "></icon-halo>
    </div>
  </div>
</template>

<script>
import asyncOperations from "@/client/extensions/composition/asyncOperations";
import layoutComposition from "@/client/extensions/composition/layout";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import { useMeta } from "vue-meta";
import { mapGetters } from "vuex";
import { watchEffect, computed, inject, ref, provide } from "vue";
import useNetworkQuality from "@/client/extensions/composition/useNetworkQuality";

export default {
  components: {},
  provide() {
    return {
      $saffronComponent: this,
      windowEvents: {
        on(event, callback) {
          if (utilities.isSSR()) {
            return this;
          }
          window.addEventListener(event, callback);
          return this;
        },
        off(event, callback) {
          if (utilities.isSSR()) {
            return this;
          }
          window.removeEventListener(event, callback);
        },
      },
      $cookie: this.$cookie,
      meta: this.metaManager,
      getMeta: () => {
        return this.metaManager;
      },
    };
  },
  props: {
    useLayouts: {
      type: Boolean,
      default: true,
    },
    useAsyncLoadBar: {
      type: Boolean,
      default: true,
    },
    useGlobalSpinner: {
      type: Boolean,
      default: true,
    },
    useMeta: {
      type: Boolean,
      default: true,
    },
    defaultMeta: {
      type: Object,
      default: () => ({}),
    },
    showSiteCredits: {
      type: Boolean,
      default: false,
    },
    siteCreditsProps: {
      type: Object,
      default: () => {},
    },
    showNetworkQuality: {
      type: Boolean,
      default: false,
    },
    networkQualityOptions: {
      type: Object,
      default: () => {
        return {};
      },
    },
    showAdminBadge: {
      type: Boolean,
      default: true,
    },
    adminBadgeProps: {
      type: Object,
      default: () => {
        return {};
      },
    },
  },
  setup(props) {
    let result = {};

    let isPageMounted = ref(false);
    provide("isPageMounted", isPageMounted);
    result.isPageMounted = isPageMounted;

    let networkQualityComposition = useNetworkQuality({
      ...props.networkQualityOptions,
    });

    result = {
      ...result,
      networkQuality: networkQualityComposition.networkQuality,
      isUpdating: networkQualityComposition.isUpdating,
      updateNetworkQuality: networkQualityComposition.updateNetworkQuality,
      pauseNetworkChecks: networkQualityComposition?.pause,
      startNetworkChecks: networkQualityComposition?.start,
      isNetworkQualityChecked: networkQualityComposition.networkQualityChecked,
    };

    if (props.showNetworkQuality && !utilities.isSSR()) {
      networkQualityComposition?.start();
      if (!utilities.isSSR()) {
        setTimeout(() => {
          networkQualityComposition?.updateNetworkQuality();
        }, 10000);
      }
    }

    let { asyncOps, asyncStatus } = asyncOperations();
    result = { ...result, asyncOps, asyncStatus };

    // meta handling
    if (props.useMeta) {
      let { t } = useI18n();
      let { meta } = useMeta({
        title: t("core.meta.defaultTitle"),
        description: t("core.meta.defaultDescription"),
        sitename: {
          tag: "meta",
          name: "sitename",
          property: "sitename",
          content: t("core.meta.siteName"),
        },
        "og:type": {
          tag: "meta",
          name: "og:type",
          property: "og:type",
          content: "website",
        },
        "og:site_name": {
          tag: "meta",
          name: "sitename",
          property: "site_name",
          content: t("core.meta.siteName"),
        },
        "og:title": {
          tag: "meta",
          name: "og:title",
          property: "og:title",
          content: t("core.meta.defaultTitle"),
        },
        "og:description": {
          tag: "meta",
          name: "og:description",
          property: "og:description",
          content: t("core.meta.defaultDescription"),
        },
        "og:image:width": {
          tag: "meta",
          name: "og:image:width",
          property: "og:image:width",
          content: "200",
        },
        "og:image:height": {
          tag: "meta",
          name: "og:image:height",
          property: "og:image:height",
          content: "200",
        },
        "og:image:type": {
          tag: "meta",
          name: "og:image:type",
          property: "og:image:type",
          content: "200",
        },
        "og:image": {
          tag: "meta",
          name: "og:image",
          property: "og:image",
          content:
            process.env.VUE_APP_ROOT_URL +
            "/" +
            process.env.VUE_APP_APPLICATION_NAME +
            "/images/defaultOgImage.png",
        },
        charset: { tag: "meta", charset: "utf8" },
        generator: { tag: "meta", content: "Saffron - spice for your code" },
        "theme-color": {
          tag: "meta",
          name: "theme-color",
          content: config.themeColor,
        },
        ...props.defaultMeta,
      });

      // watch slug, and update meta accordingly
      watchEffect(() => {
        meta.sitename.content = t("core.meta.siteName");
        meta.title = t("core.meta.defaultTitle");
        meta.description = t("core.meta.defaultDescription");
      });

      result = { ...result, metaManager: meta };
    }

    if (props.useLayouts) {
      let { finalLayout, finalLayoutRaw, layoutParams } = layoutComposition(
        props,
        useRouter()
      );
      result = { ...result, finalLayout, finalLayoutRaw, layoutParams };
    }

    result.isSaffronHydrating = inject("isSaffronHydrating");
    return result;
  },
  data: function () {
    return {
      isMounted: false,
      isMountedForAWhile: false, // we can use this to "delay" display of some stuff
      socket: null,
      isPageRtl: computed(() => {
        let val = this.isLanguageRtl();
        if (
          val &&
          typeof val === "object" &&
          typeof val.value !== "undefined"
        ) {
          return val.value;
        } else {
          return val;
        }
      }),
      shortLocaleTag: computed(() => {
        let val = this.getLocaleShort();
        if (
          val &&
          typeof val === "object" &&
          typeof val.value !== "undefined"
        ) {
          return val.value;
        } else {
          return val;
        }
      }),
      showDisconnectedModal: false,
      initialUserFetchPromise:
        this.$store.getters["user/initialFetchCompletePromise"],
      initialUserFetchComplete:
        this.$store.getters["user/initialFetchComplete"],
      modals: {},
      notifications: {},
      enableRouteTransitionAnimation: true,
      lastErrorInstance: false,
      useGtm: config.gtm.enable,
      gtmId: config.gtm.id,
      isSSR: utilities.isSSR(),
      navigations: 0,
      hasNavigated: false,
      debugItems: {},
      isShowingDebug: false,
      debugFullWidth: false,
      lastDebugItemIdentifier: undefined,
      userRequestsToHideNetworkQuality: false,
      isDev: config.env === "dev" || config.env === "development",
      isStage:
        config.env === "dev" ||
        config.env === "development" ||
        config.env === "stage" ||
        config.env === "staging",
    };
  },
  computed: {
    shouldAnimateRouter() {
      // we can consider not giving the navigation count condition, if not using SSR at all
      // this will result in fade in for initial page
      return (
        this.navigations > 1 &&
        this.enableRouteTransitionAnimation &&
        !this.$store.isSaffronHydrating
      );
    },
    routerTransitionClassIn() {
      return this.shouldAnimateRouter ? config.style.pageTransitionClassIn : "";
    },
    routerTransitionClassOut() {
      return this.shouldAnimateRouter
        ? config.style.pageTransitionClassOut
        : "";
    },
    layoutTransitionClassIn() {
      return this.shouldAnimateRouter ? config.style.pageTransitionClassIn : "";
    },
    layoutTransitionClassOut() {
      return this.shouldAnimateRouter
        ? config.style.pageTransitionClassOut
        : "";
    },
    hasDebugItems() {
      return this.debugItems && Object.entries(this.debugItems).length > 0;
    },
    ...mapGetters({
      globalSpinnerActive: "ui/isGlobalSpinnerActive",
      globalSpinnerText: "ui/globalSpinnerText",
      globalSpinnerTextParams: "ui/globalSpinnerTextParams",
      userAuthError: "user/authError",
    }),
    downQuality() {
      if (!this.showNetworkQuality) {
        return false;
      }

      return this.networkQuality?.down;
    },
    upQuality() {
      if (!this.showNetworkQuality) {
        return false;
      }

      return this.networkQuality?.up;
    },
    computedNetworkQuality() {
      if (!this.showNetworkQuality) {
        return {
          speed: 0,
          grade: 0,
          label: "core.networkQuality.notChecked",
        };
      }

      let upGrade = this?.upQuality?.grade;
      let downGrade = this?.downQuality?.grade;

      if (upGrade > downGrade) {
        return this.downQuality;
      } else {
        return this.upQuality;
      }

      // find the worst of the two connection grades and return that
    },
    networkQualityDisplayData() {
      let quality = this.computedNetworkQuality;
      let theme, isOk;
      switch (quality.grade) {
        case 4:
        case 3:
          theme = "success";
          isOk = true;
          break;
        case 2:
          theme = "warning";
          isOk = true;
          break;
        case 1:
        case 0:
          theme = "danger";
          isOk = false;
          break;
        default:
          break;
      }
      return {
        theme: theme,
        isOk: isOk,
        tooltipText: this.tu("core.networkQuality.networkQualityTooltip", {
          downSpeed: this.downQuality?.speed
            ? Math.round(Number(this.downQuality.speed))
            : "-",
          upSpeed: this.upQuality?.speed
            ? Math.round(Number(this.upQuality.speed))
            : "-",
          qualityText: this.t(quality.label),
        }),
      };
    },
    isAdminPage() {
      let name = this.$route.name;
      return typeof name === "string" && name.startsWith("admin");
    },
    computedShowSiteCredits() {
      if (!this.showSiteCredits) {
        return false;
      }

      if (utilities.isSSR()) {
        return true;
      }
      return this.isMountedForAWhile;
    },
    elevatedPermissionAdditionalUser() {
      return this.$store.getters["user/who"]("hasGroup", "superUsers");
    },
  },

  watch: {
    async userAuthError(newVal, oldVal) {
      // on auth error, show modal and logout the user. error is considered handled
      if (newVal && !oldVal) {
        this.$store.commit("user/setAuthError", false);
        await this.$store.dispatch("user/logout");

        if (!config.user.newSessionReconnectSilentErrors) {
          this.showDisconnectedModal = true;
        }
      }
    },
    isPageRtl: {
      handler(newVal) {
        let finalValue = newVal;
        if (
          typeof newVal === "object" &&
          newVal &&
          typeof newVal.value !== "undefined"
        ) {
          finalValue = newVal.value;
        }
        if (utilities.isSSR()) {
          return;
        }

        if (finalValue) {
          document.querySelector("html").setAttribute("dir", "rtl");
        } else {
          document.querySelector("html").setAttribute("dir", "ltr");
        }
      },
      immediate: true,
    },
    shortLocaleTag: {
      handler() {
        if (utilities.isSSR()) {
          return;
        }

        document
          .querySelector("html")
          .setAttribute("lang", this.shortLocaleTag);
      },
      immediate: true,
    },
    $route: {
      handler(newRoute) {
        this.navigations = this.navigations + 1;
        this.isPageMounted = false;
        this.ss("route.isPageMounted", true);
      },
      deep: true,
    },
    showNetworkQuality(newVal) {
      if (utilities.isSSR()) {
        return;
      }
      if (newVal) {
        this.startNetworkChecks();
      } else {
        this.pauseNetworkChecks();
      }
    },
  },
  async created() {
    // provide self to saffron framework
    if (!this.$root.$saffronComponent) {
      this.$root.$saffronComponent = this;
    }

    this.setSaffronComponent(this);

    if (config.useDefaultSocketFunctionality && !utilities.isSSR()) {
      // listen to unexpected disconnects - refresh token revocations
      this.socket = await this.asyncOps.socket.init();
      await this.socket.waitForUUID();

      this.socket.on("refreshTokensRevokedRemotely", async (data) => {
        let ourToken = this.$store.getters["user/refreshToken"];
        if (data.deletedTokens.includes(ourToken)) {
          this.$s.ui.notification(
            "core.user.remoteRefreshTokenRevocationNotification",
            "warning"
          );
          await this.$store.dispatch("user/logout");
          this.$router.push(config.user.saffronUser.defaultLogoutRedirect);
        }
      });

      this.socket.on("serverRestartPreparing", (data) => {
        this.isClearingCache = false;
        this.$s.ui.notification.clearAll();
        this.$s.ui.notification(
          "admin.restartServer.preparingNotification",
          "info"
        );
      });

      this.socket.on("serverRestartStarting", (data) => {
        this.isClearingCache = false;
        this.$s.ui.notification.clearAll();
        this.$s.ui.notification(
          "admin.restartServer.restartingNotification",
          "info"
        );
      });

      this.socket.on("serverOnline", (data) => {
        this.isClearingCache = false;
        this.$s.ui.notification.clearAll();
        this.$s.ui.notification(
          "admin.serverStart.serverOnlineNotification",
          "success"
        );
        this.resetI18n().then(() => {
          this.i18nReRender();
        });
      });
    }

    if (config.ecommerce.currency.useConversions) {
      // waiting is only required on ssr, and this is not done from this mehtod
      this.$store.dispatch("currency/fetchExchangeRates");
    }
  },
  updated() {},
  mounted() {
    this.$store.isSaffronMounted = true;
    this.isMounted = true;
    utilities.setClientTimeout(() => {
      this.isMountedForAWhile = true;
    }, 250);
  },

  errorCaptured(error, instance, info) {
    const name = instance.$options.name;

    const errorAsString = error ? error.toString() : "";
    const isRenderError = info === "render function";
    let errorInformationString = "";
    let errorStringName = name || "unknown name";
    // make error information

    // should redirect: redirect if error is in page or is a render error
    errorInformationString += `Message: ${error?.message}<br>`;
    errorInformationString += `Hook information: ${info}<br>`;
    errorInformationString += `In: ${errorStringName}<br>`;
    warn("saffron cought an error", { error, instance, info });
    utilities.safeLog(error);

    let isDev = config.env === "dev" || config.env === "development";

    if (isDev && isRenderError) {
      this.redirectToErrorPage(errorInformationString, errorAsString);
    }

    if (isDev && !isRenderError) {
      this.informAboutError(error.message);
    }

    return false;
  },
  methods: {
    setDebugItem(data, label, conf = {}, identifier = undefined) {
      if (!identifier) {
        identifier = `debug-item-${utilities.getUniqueNumber()}`;
      }
      this.debugItems[identifier] = {
        data: data,
        label: label,
        identifier: identifier,
        conf,
      };

      if (utilities.hasProperty(conf, "open") && conf.open === true) {
        this.openDebugDrawer();
      }
      this.lastDebugItemIdentifier = identifier;

      return identifier;
    },
    getLastDebugItemIdentifier() {
      return this.lastDebugItemIdentifier;
    },
    removeDebugItem(identifier) {
      if (this.debugItems[identifier]) {
        delete this.debugItems[identifier];
      }

      return true;
    },
    openDebugDrawer() {
      this.isShowingDebug = true;
    },
    closeDebugDrawer() {
      this.isShowingDebug = false;
    },
    toggleDebugDrawer() {
      this.isShowingDebug = !this.isShowingDebug;
    },
    clearDebugItems() {
      this.debugItems = {};
      return true;
    },
    registerModal(conf) {
      // parse conf
      let uid = utilities.getUniqueNumber();

      this.modals[uid] = {
        uid: uid,
        type: conf.options.type || "alert",
        promptValue: conf.options.initialValue || null,
        ...conf,
      };
    },
    modalStackCloseHandler(arg) {
      let modalConf = this.modals[arg.uid];

      if (!modalConf) {
        return;
      }

      // try to fulfil or reject the modal promise, if available
      if (arg.actionType === "fulfil" && modalConf.fulfil) {
        modalConf.fulfil(arg.data);
      }

      if (arg.actionType === "reject" && modalConf.reject) {
        modalConf.reject();
      }

      delete this.modals[arg.uid];
    },
    registerNotification(options) {
      let id = utilities.getUniqueNumber();
      options.ref = "notification-" + id;
      this.notifications[id] = { ...options, id };
    },
    notificationClosedHandler(id) {
      if (!this.notifications[id]) {
        return;
      }

      if (
        this.notifications[id].fulfil &&
        typeof this.notifications[id].fulfil === "function"
      ) {
        this.notifications[id].fulfil();
      }

      delete this.notifications[id];
    },
    closeAllNotifications() {
      if (!this.notifications || typeof this.notifications !== "object") {
        return;
      }

      for (const conf of Object.values(this.notifications)) {
        // case of array ref
        if (
          conf.ref &&
          this.$refs[conf.ref] &&
          Array.isArray(this.$refs[conf.ref]) &&
          this.$refs[conf.ref].length === 1
        ) {
          this.$refs[conf.ref][0].close();
        }

        // case of "regular" ref. I do not know if this can actually happen, tbh.
        if (
          conf.ref &&
          this.$refs[conf.ref] &&
          !Array.isArray(this.$refs[conf.ref])
        ) {
          this.$refs[conf.ref].close();
        }
      }
    },
    acceptDisconnect() {
      this.showDisconnectedModal = false;
      this.$router.go();
    },
    getSafeNotificationConfig(conf) {
      let result = { ...conf };

      ["id", "fulfil", "reject"].forEach((key) => {
        if (utilities.hasProperty(conf, key)) {
          delete result[key];
        }
      });
      return result;
    },
    redirectToErrorPage(errorInformation = false, errorAsString = false) {
      this.$router.push({
        name: "error",
        params: {
          technicalInformationHtml: errorInformation,
          errorAsString: errorAsString,
        },
      });
    },
    informAboutError() {
      this.$s.ui.notification("core.errorCatchNotification", "error");
    },
    isNonEmptyString(data) {
      return utilities.isNonEmptyString(data);
    },
    handlePageMounted(e) {
      this.isPageMounted = true;
    },
    async relogAsAdmin() {
      this.$s.ui.globalSpinner.show(true);
      let user = this.elevatedPermissionAdditionalUser;

      let uuid = user.user.profile.uuid;
      this.$store.commit("user/restorePersistedUserByUuid", uuid);
      await this.$store.dispatch("user/updateUserProfile");
      await this.$store.dispatch("user/refreshJwt");

      this.$s.ui.globalSpinner.show(false);
      this.$router.push("/");
      this.$s.ui.notification(
        "core.elevatedUserFloatingBadge.reconnectedAsAdmin",
        "success"
      );
    },
  },
  async serverPrefetch() {
    let promises = [];

    let baseUserPromise = new Promise((resolve) => {
      // when we get user prefetch - release this hook
      watchEffect(() => {
        if (this.$store.getters["user/initialFetchComplete"]) {
          resolve();
        }
      });
    });
    let userPromise = utilities.promiseWithTimeLimit(baseUserPromise, 2500).catch(e =>{});
    promises.push(userPromise);

    if (config.ecommerce.currency.useConversions) {
      let baseExchangeRatesPromise = new Promise((resolve) => {
        watchEffect(() => {
          if (this.$store.getters["currency/areExchangeRatesLoaded"]) {
            resolve();
          }
        });
      });
      let exchangeRatesPromise = utilities.promiseWithTimeLimit(
        baseExchangeRatesPromise,
        1500
      ).catch(e =>{});
      promises.push(exchangeRatesPromise);
    }

    let result = await Promise.allSettled(promises).catch((e) => {});

    return result;
  },
};
</script>

<style scoped lang="scss">
.debug-floater {
  transition: 150ms ease;
  box-shadow: var(--box-shadow-2);

  &:hover {
    box-shadow: var(--box-shadow-5);
    transform: scale(1.1, 1.1);
  }
}

.floating-widgets {
  display: flex;
  position: fixed;
  gap: var(--margin-m);
  z-index: var(--z-top-1);
  inset-inline-end: var(--margin-l);
  bottom: var(--margin-l);
}
</style>

<style lang="scss">
#notifications {
  position: fixed;
  top: 0;
  inset-inline-start: 0;
  inset-inline-end: 0;
  width: auto;
  height: 0;
  overflow: visible;
}

#snackbar {
  position: fixed;
  bottom: 0;
  inset-inline-start: 0;
  height: auto;
  width: auto;
  max-width: 700px;
  overflow: visible;
  padding-inline-start: clamp(
    10px,
    var(--margin-m),
    var(--margin-m)
  ); // support weUI with default fallback fallback

  @media screen and (max-width: 800px) {
    max-width: 90vw;
  }
}
</style>
